import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { formatDate } from "../../helpers/formatDate";
import { getAnnualClientActivityReviewQueue } from "../../services/client/getAnnualClientActivityReviewQueue";
import { formatInactiveDuration } from "../../pages/Dashboards/InactiveReport/inactiveReportUtils";
import AnnualClientActivityReviewContent from "./AnnualClientActivityReviewContent";

const PAGE_SIZE = 50;

const getClientId = (client) =>
    client?.clientId ?? client?.ClientId ?? client?.id ?? client?.Id;
const getClientName = (client) =>
    client?.clientName ?? client?.ClientName ?? client?.name ?? client?.Name ?? "Client";
const getLastActivity = (client) =>
    client?.operationalLastActivityAt ?? client?.OperationalLastActivityAt ??
    client?.lastActivityAt ?? client?.LastActivityAt;

const getQueuePage = (response) => {
    const data = response?.data ?? {};
    const clients = data?.clients ?? data?.items ?? data?.reviewQueue ??
        data?.results ?? (Array.isArray(data) ? data : []);
    const totalCount = Number(
        data?.totalCount ?? data?.TotalCount ?? data?.count ?? data?.Count ?? clients.length,
    );
    return {
        clients: Array.isArray(clients) ? clients : [],
        totalCount: Number.isFinite(totalCount) ? totalCount : 0,
    };
};

const getClientType = (client) => {
    const value = client?.clientType ?? client?.ClientType ?? client?.clientDetail?.clientType;
    if (value === null || value === undefined || value === "") return "—";
    const normalized = String(value).trim().toLowerCase();
    if (normalized === "1" || normalized === "individual") return "Individual";
    if (normalized === "2" || normalized === "business") return "Business";
    return String(value).replace(/[_-]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const getTeamNames = (client) => {
    const teams = client?.teams ?? client?.Teams ?? client?.teamNames ?? [];
    const values = Array.isArray(teams) ? teams : teams ? [teams] : [];
    const names = values.map((team) => typeof team === "string" ? team :
        team?.teamName ?? team?.TeamName ?? team?.name ?? team?.Name).filter(Boolean);
    return [...new Set(names)].join(", ") || "—";
};

const loadQueue = async () => {
    const firstResponse = await getAnnualClientActivityReviewQueue({ page: 1, pageSize: PAGE_SIZE });
    if (!firstResponse?.success) return firstResponse;

    const firstPage = getQueuePage(firstResponse);
    const pages = Math.max(1, Math.ceil(firstPage.totalCount / PAGE_SIZE));
    const allClients = [...firstPage.clients];
    for (let page = 2; page <= pages; page += 1) {
        const response = await getAnnualClientActivityReviewQueue({ page, pageSize: PAGE_SIZE });
        if (!response?.success) return response;
        allClients.push(...getQueuePage(response).clients);
    }

    const seen = new Set();
    return {
        success: true,
        clients: allClients.filter((client) => {
            const id = getClientId(client);
            if (!id || seen.has(String(id))) return false;
            seen.add(String(id));
            return true;
        }),
    };
};

const ClientSummary = ({ client }) => {
    const lastActivity = getLastActivity(client);
    const formattedActivity = formatDate(lastActivity, { dateStyle: "medium" });
    const inactiveFor = formatInactiveDuration(lastActivity);
    return (
        <div className="mb-5 rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-lg font-semibold text-slate-900">{getClientName(client)}</p>
            <dl className="mt-3 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                <div><dt className="font-medium text-slate-500">Client type</dt><dd className="text-slate-800">{getClientType(client)}</dd></div>
                <div><dt className="font-medium text-slate-500">Teams</dt><dd className="text-slate-800">{getTeamNames(client)}</dd></div>
                <div><dt className="font-medium text-slate-500">Last activity</dt><dd className="text-slate-800">{formattedActivity || "No activity recorded"}</dd></div>
                <div><dt className="font-medium text-slate-500">Inactive for</dt><dd className="text-slate-800">{inactiveFor || "—"}</dd></div>
            </dl>
        </div>
    );
};

const BulkClientActivityReview = ({
    open,
    canWriteClientServices,
    canDeleteClientServices,
    onCountRefresh,
    onSessionEnd,
    onAfterLeave,
}) => {
    const [queueSnapshot, setQueueSnapshot] = useState([]);
    const [currentClientIndex, setCurrentClientIndex] = useState(0);
    const [reviewedClientIds, setReviewedClientIds] = useState(new Set());
    const [changedClientIds, setChangedClientIds] = useState(new Set());
    const [isLoading, setIsLoading] = useState(false);
    const [isMutationPending, setIsMutationPending] = useState(false);
    const [error, setError] = useState("");
    const reviewedIdsRef = useRef(new Set());
    const changedIdsRef = useRef(new Set());
    const sessionEndedRef = useRef(false);
    const requestGenerationRef = useRef(0);

    useEffect(() => {
        if (!open) return;
        const generation = requestGenerationRef.current + 1;
        requestGenerationRef.current = generation;
        sessionEndedRef.current = false;
        reviewedIdsRef.current = new Set();
        changedIdsRef.current = new Set();
        setReviewedClientIds(new Set());
        setChangedClientIds(new Set());
        setQueueSnapshot([]);
        setCurrentClientIndex(0);
        setIsMutationPending(false);
        setError("");
        setIsLoading(true);

        loadQueue().then((result) => {
            if (requestGenerationRef.current !== generation) return;
            setIsLoading(false);
            if (!result?.success) {
                setError(result?.message || "The client review queue could not be loaded.");
                return;
            }
            setQueueSnapshot(result.clients);
        });

        return () => {
            requestGenerationRef.current += 1;
        };
        // A new false -> true open transition defines a fresh session.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const endSession = useCallback(() => {
        if (sessionEndedRef.current) return;
        sessionEndedRef.current = true;
        onSessionEnd?.({
            changedCount: changedIdsRef.current.size,
            reviewedCount: reviewedIdsRef.current.size,
        });
    }, [onSessionEnd]);

    const requestClose = () => {
        if (!isMutationPending) endSession();
    };

    const handleCompleted = async (outcome) => {
        const client = queueSnapshot[currentClientIndex];
        const clientId = getClientId(client);
        if (!clientId) return;

        const nextReviewed = new Set(reviewedIdsRef.current);
        nextReviewed.add(String(clientId));
        reviewedIdsRef.current = nextReviewed;
        setReviewedClientIds(nextReviewed);

        if (outcome?.changesApplied === true) {
            const nextChanged = new Set(changedIdsRef.current);
            nextChanged.add(String(clientId));
            changedIdsRef.current = nextChanged;
            setChangedClientIds(nextChanged);
            onCountRefresh?.();
        }

        if (currentClientIndex + 1 >= queueSnapshot.length) {
            endSession();
        } else {
            setCurrentClientIndex((index) => index + 1);
        }
    };

    const currentClient = queueSnapshot[currentClientIndex];
    const clientId = getClientId(currentClient);
    const backendCanDelete = currentClient?.canDeleteServices ?? currentClient?.CanDeleteServices;
    const canDeleteCurrentClientServices =
        canDeleteClientServices && backendCanDelete !== false;
    const progress = queueSnapshot.length > 0
        ? ((currentClientIndex + 1) / queueSnapshot.length) * 100
        : 0;

    return (
        <Transition.Root show={open} as={Fragment} afterLeave={onAfterLeave}>
            <Dialog as="div" className="relative z-[200]" onClose={requestClose}>
                <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-slate-900/60" />
                </Transition.Child>
                <div className="fixed inset-0 z-[210] overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Dialog.Panel className="w-full max-w-2xl rounded-lg bg-white p-5 shadow-xl sm:p-6">
                            <div className="flex items-start justify-between gap-4">
                                <Dialog.Title className="text-xl font-semibold text-slate-900">Semiannual Client Review</Dialog.Title>
                                <button type="button" onClick={requestClose} disabled={isMutationPending}
                                    className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-40">
                                    <span className="sr-only">Close bulk review</span>
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>

                            {isLoading ? (
                                <div className="flex min-h-[260px] items-center justify-center" role="status">
                                    <div className="text-center text-sm text-slate-600">
                                        <span className="mx-auto mb-3 block h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-teal-500" />
                                        Loading review queue...
                                    </div>
                                </div>
                            ) : error ? (
                                <div className="mt-5">
                                    <div role="alert" className="rounded-md bg-red-50 p-4 text-sm text-red-700">{error}</div>
                                    <button type="button" onClick={requestClose} className="mt-4 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Close</button>
                                </div>
                            ) : currentClient ? (
                                <>
                                    <div className="mb-5 mt-4" aria-label={`Client ${currentClientIndex + 1} of ${queueSnapshot.length}`}>
                                        <div className="flex items-center justify-between text-sm font-semibold text-teal-700">
                                            <span>Client {currentClientIndex + 1} of {queueSnapshot.length}</span>
                                            <span>{reviewedClientIds.size} reviewed</span>
                                        </div>
                                        <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200">
                                            <div className="h-full rounded-full bg-teal-500 transition-[width]" style={{ width: `${progress}%` }} />
                                        </div>
                                    </div>
                                    <ClientSummary client={currentClient} />
                                    <AnnualClientActivityReviewContent
                                        key={String(clientId)}
                                        clientId={clientId}
                                        clientName={getClientName(currentClient)}
                                        canWriteClientServices={canWriteClientServices}
                                        canDeleteClientServices={canDeleteCurrentClientServices}
                                        onCompleted={handleCompleted}
                                        celebrateServiceCompletions={false}
                                        onMutationStateChange={setIsMutationPending}
                                        onRequestClose={requestClose}
                                    />
                                    <span className="sr-only">{changedClientIds.size} clients changed</span>
                                </>
                            ) : (
                                <div className="py-12 text-center">
                                    <p className="font-medium text-slate-900">No clients currently need review.</p>
                                    <button type="button" onClick={requestClose} className="mt-5 rounded-md bg-teal-500 px-4 py-2 text-sm font-medium text-white hover:bg-teal-600">Close</button>
                                </div>
                            )}
                        </Dialog.Panel>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
};

export default BulkClientActivityReview;
